import { useState } from "react";
import { useHaptics } from "@/hooks/use-haptics";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type Conversion = { label: string; units: { name: string; factor: number }[] };

const conversions: Conversion[] = [
  { label: "Length", units: [{ name: "Inches", factor: 1 }, { name: "Feet", factor: 1/12 }, { name: "mm", factor: 25.4 }, { name: "cm", factor: 2.54 }, { name: "Meters", factor: 0.0254 }] },
  { label: "Pressure", units: [{ name: "PSI", factor: 1 }, { name: "kPa", factor: 6.89476 }, { name: "Bar", factor: 0.0689476 }, { name: "inHg", factor: 2.03602 }, { name: "ft H₂O", factor: 2.30666 }] },
  { label: "Temperature", units: [{ name: "°F", factor: 1 }, { name: "°C", factor: 1 }] },
  { label: "Power", units: [{ name: "BTU/hr", factor: 1 }, { name: "Watts", factor: 0.29307 }, { name: "kW", factor: 0.00029307 }, { name: "Tons", factor: 1/12000 }] },
  { label: "Flow", units: [{ name: "GPM", factor: 1 }, { name: "L/min", factor: 3.78541 }, { name: "CFM", factor: 0.13368 }, { name: "m³/hr", factor: 0.22712 }] },
  { label: "Electrical", units: [{ name: "Amps @120V", factor: 1 }, { name: "Watts", factor: 120 }, { name: "Amps @240V", factor: 0.5 }, { name: "kVA 1Φ", factor: 0.12 }] },
];

const InlineConverter = () => {
  const [selectedConv, setSelectedConv] = useState(0);
  const [fromUnit, setFromUnit] = useState(0);
  const [inputVal, setInputVal] = useState("");
  const { lightTap } = useHaptics();

  const conv = conversions[selectedConv];
  const isTemp = conv.label === "Temperature";

  const convert = (value: number, fromIdx: number, toIdx: number): string => {
    if (isTemp) {
      const fromF = fromIdx === 0 ? value : value * 9 / 5 + 32;
      const result = toIdx === 0 ? fromF : (fromF - 32) * 5 / 9;
      return result.toFixed(2);
    }
    const baseValue = value / conv.units[fromIdx].factor;
    return (baseValue * conv.units[toIdx].factor).toFixed(4).replace(/\.?0+$/, "");
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Select value={String(selectedConv)} onValueChange={(v) => { lightTap(); setSelectedConv(Number(v)); setFromUnit(0); setInputVal(""); }}>
          <SelectTrigger className="bg-secondary text-secondary-foreground border-border h-9 text-xs flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {conversions.map((c, i) => <SelectItem key={i} value={String(i)}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(fromUnit)} onValueChange={(v) => { lightTap(); setFromUnit(Number(v)); }}>
          <SelectTrigger className="bg-secondary text-secondary-foreground border-border h-9 text-xs flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {conv.units.map((u, i) => <SelectItem key={i} value={String(i)}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          type="number"
          inputMode="decimal"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          placeholder="0"
          className="bg-secondary text-foreground border-border font-mono text-sm h-9 w-24"
        />
      </div>
      {inputVal && !isNaN(Number(inputVal)) && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {conv.units.map((u, i) => i !== fromUnit && (
            <div key={i} className="flex justify-between items-center bg-secondary/50 rounded-lg px-2.5 py-1.5">
              <span className="text-[11px] text-muted-foreground">{u.name}</span>
              <span className="font-mono text-sm text-primary font-bold">{convert(Number(inputVal), fromUnit, i)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InlineConverter;
